/** @jsx jsx */
import {
  Modal,
  ModalActionButton,
  ModalContainer,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalHeading,
  ModalProps,
} from "@eworkit/iris";
import { Form, Formik } from "formik";
import { FC, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { unstable_useId as useId } from "reakit/Id";
import { jsx } from "theme-ui";

import { getTimeReportProjectItem, TimeReport, TimeReportProjectItemPath } from "@/data/time-reports";
import { useToasts } from "@/views/components/Toast/useToasts";

import { useTimeReportEdit } from "../edit/TimeReportEditContext";
import {
  TimeReportSpecificationFormFields,
  TimeReportSpecificationFormValues,
  TIME_REPORT_SPECIFICATION_FORM_DEFAULT_VALUES,
  TIME_REPORT_SPECIFICATION_VALIDATION_SCHEMA,
} from "./TimeReportSpecificationFormFields";

export interface TimeReportSpecificationModalProps extends ModalProps {
  timeReport: TimeReport;
  itemPath: TimeReportProjectItemPath;
}

export const TimeReportSpecificationModal: FC<TimeReportSpecificationModalProps> = ({
  timeReport,
  itemPath,
  ...modalProps
}) => {
  const { t } = useTranslation("session");
  const { addToast } = useToasts();
  const { id: formId } = useId();

  const { updateItem } = useTimeReportEdit();

  const [loading, setLoading] = useState(false);

  const item = useMemo(() => getTimeReportProjectItem(timeReport, itemPath), [timeReport, itemPath]);

  const handleSubmit = useCallback(
    async (values: TimeReportSpecificationFormValues) => {
      try {
        setLoading(true);

        await updateItem(itemPath, i => ({
          ...i,
          comment: values.comment ?? null,
          attachments: values.attachments.filter(i => i.attachment).map(i => i.attachment!),
        }));

        modalProps.dialogState.hide();
      } catch (error) {
        addToast({
          text: t("common:ERROR_MESSAGES.GENERAL"),
          type: "danger",
        });
      } finally {
        setLoading(false);
      }
    },
    [itemPath, updateItem, modalProps, addToast, t],
  );

  return (
    <Modal aria-label={t("TIME_REPORTS.SPECIFICATION.EDIT_SPECIFICATION")} {...modalProps}>
      <ModalContainer>
        <ModalHeader
          onCloseClick={modalProps.dialogState.hide}
          closeButtonAriaLabel={t("COMPONENTS.MODAL.CLOSE")}
          headerPlaceholder={<ModalHeading>{t("TIME_REPORTS.SPECIFICATION.EDIT_SPECIFICATION")}</ModalHeading>}
        />

        <ModalContent>
          <Formik<TimeReportSpecificationFormValues>
            initialValues={{
              ...TIME_REPORT_SPECIFICATION_FORM_DEFAULT_VALUES,
              comment: item?.comment ?? "",
              attachments: item?.attachments?.map(attachment => ({ attachment })) ?? [],
            }}
            validationSchema={TIME_REPORT_SPECIFICATION_VALIDATION_SCHEMA}
            onSubmit={handleSubmit}>
            {formik => (
              <Form id={formId}>
                <TimeReportSpecificationFormFields formik={formik} />
              </Form>
            )}
          </Formik>
        </ModalContent>

        <ModalFooter>
          <ModalActionButton type="button" variant="ghost" onClick={modalProps.dialogState.hide}>
            {t("TIME_REPORTS.SPECIFICATION.CANCEL")}
          </ModalActionButton>
          <ModalActionButton
            type="submit"
            form={formId}
            variant="primary"
            loading={loading}
            disabled={loading}
            focusable={!loading}>
            {t("TIME_REPORTS.SPECIFICATION.SAVE")}
          </ModalActionButton>
        </ModalFooter>
      </ModalContainer>
    </Modal>
  );
};
